import React, { memo, Children, cloneElement, useEffect, useRef, useCallback, Fragment } from 'react';
import cx from 'classnames';
import Text, { TextSize } from '@move-ui/text';
import { CloseIcon } from '@move-ui/icons-asset-catalog';
import styles from './MobileMenu.module.css';
import { useDropdownKeyboardSupport } from '../hooks/useDropdownKeyboardSupport';
import { scrollListToSelectedOption } from '../utils/scrollListToSelectedOption';
import { focusOption } from '../utils/focusOption';
import { Search } from '../Search/Search';
export const MobileMenu = memo(({ children, open, label, onClose, 'data-testid': testId, footer, multiple, searchProps, noResultsComponent, scrollTopKey }) => {
    const ref = useRef(null);
    const { onKeyDown, optionsRef } = useDropdownKeyboardSupport();
    useEffect(() => {
        if (ref.current) {
            ref.current.scrollTop = 0;
        }
    }, [scrollTopKey]);
    useEffect(() => {
        if (open && window.innerWidth < 640) {
            scrollListToSelectedOption(ref.current, optionsRef.current);
        }
    }, [open, optionsRef]);
    useEffect(() => {
        const classes = [styles.dropdownMobileMenuOpen, 'noModalOverflow'];
        if (open) {
            document.body.classList.add(...classes);
        }
        else {
            document.body.classList.remove(...classes);
        }
        return () => document.body.classList.remove(...classes);
    }, [open]);
    const onCloseKeyDown = useCallback(({ key }) => (key === ' ' || key === 'Enter') && onClose(), [onClose]);
    return (React.createElement("div", { "data-testid": testId ? `${testId}MobileMenu` : undefined, className: cx(styles.mobileMenu, {
            [styles.closed]: !open,
            [styles.maxHeight]: searchProps
        }), role: "listbox", "aria-hidden": !open, "aria-multiselectable": multiple },
        React.createElement("div", { className: styles.mobileMenuHeader },
            label && (React.createElement(Text, { size: TextSize.M, color: "neutral-100", bold: true, className: styles.mobileMenuHeaderTitle }, label)),
            React.createElement(CloseIcon, { "data-testid": testId ? `${testId}MobileMenuCloseButton` : undefined, onClick: onClose, role: "button", tabIndex: open ? 0 : -1, onKeyDown: onCloseKeyDown })),
        searchProps && (React.createElement(Fragment, null,
            React.createElement(Search, Object.assign({}, searchProps)),
            searchProps.value.length > 0 &&
                Children.count(children) < 1 &&
                noResultsComponent)),
        React.createElement("ul", { role: "menu", "aria-hidden": !open, onKeyDown: (event) => {
                var _a, _b;
                return !((_b = (_a = searchProps === null || searchProps === void 0 ? void 0 : searchProps.inputRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.contains(event.target)) &&
                    onKeyDown(event);
            }, ref: ref },
            Children.map(children, (child, index) => cloneElement(child, {
                ref: (childRef) => {
                    optionsRef.current[index] = childRef;
                },
                onClick: () => {
                    var _a, _b;
                    child.props.onClick();
                    // Refocus search after clicking on an option if search is still active
                    if ((searchProps === null || searchProps === void 0 ? void 0 : searchProps.value.length) === 0) {
                        return;
                    }
                    (_b = (_a = searchProps === null || searchProps === void 0 ? void 0 : searchProps.inputRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus();
                },
                focusPrevious: () => focusOption(ref.current, optionsRef.current[index - 1]),
                focusNext: () => focusOption(ref.current, optionsRef.current[index + 1]),
                idPrefix: 'mobile-'
            })),
            !searchProps && footer),
        searchProps && footer));
});
//# sourceMappingURL=MobileMenu.js.map